import React, { useState, useEffect } from 'react';
import { Card, Table, Modal, Button, Form, Alert } from 'react-bootstrap';
import PageHeader1 from '../../components/common/PageHeader1';
import EditButton from "../../components/common/EditButton";
import DeleteButton from "../../components/common/DeleteButton";
import { toast } from 'react-toastify';
import apiClient from '../../utils/api';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Pagination } from 'react-bootstrap';

const STATUS_COLORS = {
    parked: 'info',
    requested: 'warning',
    dispatched: 'primary',
    delivered: 'success'
};

const STATUS_ACTIONS = {
    parked: { next: 'requested', label: 'Request' },
    requested: { next: 'dispatched', label: 'Dispatch' },
    dispatched: [
        { next: 'delivered', label: 'Mark Delivered' },
        { next: 'parked', label: 'Mark Retrieved' }
    ]
};

function ParkingList() {
    const [parkings, setParkings] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');    
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedParking, setSelectedParking] = useState(null);
    const [selectedAction, setSelectedAction] = useState(null);
    const [paymentMode, setPaymentMode] = useState('Cash');    
    const [valetPoints, setValetPoints] = useState([]);
    const [selectedValetPoint, setSelectedValetPoint] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [calculatedCharges, setCalculatedCharges] = useState(null);
    const [isCalculatingCharges, setIsCalculatingCharges] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [perPage] = useState(10);
    const navigate = useNavigate();
    

    const fetchValetPoints = async (parkingId) => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get(`accounts/parkings/active-valet-points/${parkingId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            setValetPoints(response.data.data);
        } catch (error) {
            console.error('Error fetching Valet Points:', error);
            toast.error('Error loading Valet Points');
        }
    };

    const fetchParkings = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get(`accounts/parkings?page=${currentPage}&per_page=${perPage}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            
            const parkings = response.data.data.map(parking => ({
                ...parking,
                brand_name: parking.brand?.name ?? '',
                model_name: parking.model?.name ?? '',
                color_name: parking.color?.name ?? '',
                status: parking.status ?? 'parked',
                payment: parking.payment_status ?? 'unpaid'
            }));
            
            setParkings(parkings);
            setTotalPages(Math.ceil(response.data.total / response.data.per_page));
        } catch (error) {
            console.error('Error fetching parkings:', error.response?.data ?? error);
            toast.error(error.response?.data?.message ?? 'Error loading parking list');
            setParkings([]);
        }
    };   
    
    useEffect(() => {
        fetchParkings();
    }, [currentPage]);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this parking record?')) return;
        
        try {
            const token = localStorage.getItem('auth_token');
            await apiClient.delete(`accounts/parkings/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            toast.success('Parking record deleted successfully');
            fetchParkings(); // Refresh the list
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error deleting parking record');
        }
    };        
      
    const handleOpenPaymentModal = (parking) => {
        setSelectedParking(parking);
        setPaymentMode('Cash');
        setSelectedValetPoint('');
        fetchValetPoints(parking.id);
        fetchParkingCharges(parking.id);
        setShowPaymentModal(true);
    };
    
    const handleSavePayment = async () => {
        if (!selectedValetPoint) {
            toast.error('Please select a Valet Point');
            return;
        }

        try {
            const token = localStorage.getItem('auth_token');
            await apiClient.post(`accounts/parkings/${selectedParking.id}/payment`, {                payment_status: 'paid',
                payment_mode: paymentMode,
                amount_received: calculatedCharges ? calculatedCharges.amount : 0,
                valet_point_id: selectedValetPoint
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            toast.success('Payment updated successfully');
            setShowPaymentModal(false);
            fetchParkings(); // Refresh the list
        } catch (error) {
            console.error('Error:', error);
            const errorMessage = error.response?.data?.message || 'Error updating payment';
            toast.error(errorMessage);
        }
    };

    const handleStatusUpdate = async (parking, action = null) => {
        setSelectedParking(parking);
        setSelectedAction(action);
        setShowConfirmModal(true);
    };

    const confirmStatusUpdate = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            let nextStatus;
            
            if (selectedAction) {
                // For dispatched status with multiple actions
                nextStatus = selectedAction.next;
            } else {
                // For single action statuses
                const statusAction = STATUS_ACTIONS[selectedParking.status];
                nextStatus = Array.isArray(statusAction) ? statusAction[0].next : statusAction.next;
            }
            
            await apiClient.put(
                `accounts/parkings/${selectedParking.id}/status`,
                { status: nextStatus },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            );

            setSuccess('Status updated successfully');
            setShowConfirmModal(false);
            
            // Refresh the parking list
            fetchParkings();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update status');
        }
    };

    const fetchParkingCharges = async (parkingId) => {
        setIsCalculatingCharges(true);
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get(`accounts/parkings/${parkingId}/calculate-charges`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            setCalculatedCharges(response.data);
        } catch (error) {
            console.error('Error calculating charges:', error);
            toast.error('Error calculating parking charges');
        } finally {
            setIsCalculatingCharges(false);
        }
    };

    const handlePrintOptions = (parking) => {
        navigate(`/accounts/parking-print-options/${parking.id}`);
    };

    const filteredParkings = parkings.filter(p =>
        Object.values(p).some(value =>
            String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    const renderStatusBadge = (status) => (
        <span className={`badge bg-${STATUS_COLORS[status]}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
    );

    const renderActionButton = (parking) => {
        const action = STATUS_ACTIONS[parking.status];
        if (!action) return null;

        // Handle multiple actions for dispatched status
        if (Array.isArray(action)) {
            return (
                <div className="d-flex gap-1">
                    {action.map((actionItem, index) => (
                        <Button
                            key={index}
                            size="sm"
                            variant={actionItem.next === 'delivered' ? 'success' : 'warning'}
                            onClick={() => handleStatusUpdate(parking, actionItem)}
                        >
                            {actionItem.label}
                        </Button>
                    ))}
                </div>
            );
        }

        // Handle single action
        return (
            <Button
                size="sm"
                variant={STATUS_COLORS[action.next]}
                onClick={() => handleStatusUpdate(parking)}
            >
                {action.label}
            </Button>
        );
    };

    // Check if parking ticket has missing required information
    const hasMissingInformation = (parking) => {
        return !parking.plate_number || 
               !parking.brand_name || 
               !parking.model_name || 
               !parking.color_name;
    };

    return (
        <div className="body d-flex py-3">
            <div className="container-xxl">
                {/* <PageHeader1 
                    pagetitle="Parking List" 
                    button={true} 
                    buttonText="Add New Parking"
                    buttonLink="/parking/add"
                /> */}

                <Card className="mb-3">
                    <Card.Body>
                        <Form.Control
                            type="text"
                            placeholder="Search by ticket, mobile, plate, car, color..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body>
                        <Table responsive striped hover>
                            <thead>
                                <tr>
                                    <th>Ticket #</th>
                                    <th>Mobile Number</th>
                                    <th>Plate Number</th>
                                    <th>Car Details</th>
                                    <th>Time In</th>
                                    <th>Status</th>
                                    <th>Payment</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredParkings.map(parking => (
                                    <tr key={parking.id}>
                                        <td>{parking.ticket_number}</td>
                                        <td>{parking.mobile_number}</td>
                                        <td>{parking.plate_number}</td>
                                        <td>
                                            {parking.brand_name} {parking.model_name}<br />
                                            <small className="text-muted">{parking.color_name}</small>
                                        </td>
                                        <td>{moment(parking.created_at).format('LLL')}</td>                                        <td>
                                            {renderStatusBadge(parking.status)}
                                        </td>
                                        <td>
                                            <span className={`badge ${parking.payment === 'paid' ? 'bg-success' : 'bg-danger'}`}>
                                                {parking.payment?.charAt(0).toUpperCase() + parking.payment?.slice(1)}
                                            </span>
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-sm btn-light text-primary border me-2"
                                                title="Payment"
                                                onClick={() => handleOpenPaymentModal(parking)}
                                            >
                                                <i className="icofont-credit-card"></i>
                                            </button>
                                            <button
                                                className="btn btn-sm btn-light text-info border me-2"
                                                title="Print Options"
                                                onClick={() => handlePrintOptions(parking)}
                                            >
                                                <i className="icofont-printer"></i>
                                            </button>
                                            <button
                                                className="btn btn-sm btn-light text-secondary border me-2"
                                                title="View Details"
                                                onClick={() => navigate(`/accounts/parking/view/${parking.id}`)}
                                            >
                                                <i className="icofont-eye-alt"></i>
                                            </button>
                                            {/* Show Edit button only if required information is missing */}
                                            {hasMissingInformation(parking) && (
                                                <EditButton 
                                                    permission="parkings.edit" 
                                                    to={`${process.env.PUBLIC_URL}/accounts/parking/edit/${parking.id}`} 
                                                />
                                            )}
                                            <DeleteButton permission="parkings.delete" onDelete={() => handleDelete(parking.id)} />
                                            {renderActionButton(parking)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        <div className="d-flex justify-content-center mt-3">
                            <Pagination>
                                <Pagination.First 
                                    onClick={() => handlePageChange(1)} 
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev 
                                    onClick={() => handlePageChange(currentPage - 1)} 
                                    disabled={currentPage === 1}
                                />
                                
                                {[...Array(totalPages)].map((_, idx) => (
                                    <Pagination.Item
                                        key={idx + 1}
                                        active={idx + 1 === currentPage}
                                        onClick={() => handlePageChange(idx + 1)}
                                    >
                                        {idx + 1}
                                    </Pagination.Item>
                                ))}
                                
                                <Pagination.Next 
                                    onClick={() => handlePageChange(currentPage + 1)} 
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last 
                                    onClick={() => handlePageChange(totalPages)} 
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    </Card.Body>
                </Card>
            </div>

            {/* Payment Modal */}
            <Modal 
                show={showPaymentModal} 
                onHide={() => {
                    setShowPaymentModal(false);
                    setCalculatedCharges(null);
                }} 
                centered>
                <Modal.Header closeButton>
                    <Modal.Title>Payment Info</Modal.Title>
                </Modal.Header>
                <Modal.Body>                    
                    {selectedParking && (
                        <Form>                            <Form.Group className="mb-3">
                                <Form.Label>Total Amount</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    value={calculatedCharges ? `QR ${calculatedCharges.amount}` : 'Calculating...'} 
                                    disabled 
                                />
                                {calculatedCharges && (
                                    <Form.Text className="text-muted">
                                        Duration: {calculatedCharges.duration.hours}h {calculatedCharges.duration.minutes}m
                                        {calculatedCharges.within_grace_period && " (Within Grace Period)"}
                                    </Form.Text>
                                )}
                            </Form.Group>
                            {selectedParking.payment === 'paid' ? (
                                <>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Amount</Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            value={`QR ${selectedParking.amount_received}`} 
                                            disabled 
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Payment Mode</Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            value={selectedParking.payment_mode} 
                                            disabled 
                                        />
                                    </Form.Group>
                                </>
                            ) : (
                                <>                                    <Form.Group className="mb-3">
                                        <Form.Label>Valet Point*</Form.Label>
                                        <Form.Select 
                                            value={selectedValetPoint} 
                                            onChange={(e) => setSelectedValetPoint(e.target.value)}
                                            required
                                        >
                                            <option value="">-- Select Valet Point --</option>
                                            {valetPoints.map(point => (
                                                <option key={point.id} value={point.id}>
                                                    {point.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Payment Mode*</Form.Label>
                                        <Form.Select 
                                            value={paymentMode} 
                                            onChange={(e) => setPaymentMode(e.target.value)}
                                            required
                                        >
                                            <option value="Cash">Cash</option>
                                            <option value="Card">Card</option>
                                        </Form.Select>
                                    </Form.Group>
                                </>
                            )}
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>                    
                    <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
                        Close
                    </Button>
                    {selectedParking && selectedParking.payment !== 'paid' && (                        <Button 
                            variant="primary" 
                            onClick={handleSavePayment}
                            disabled={!selectedValetPoint || isCalculatingCharges || !calculatedCharges}
                        >
                            {isCalculatingCharges ? 'Calculating...' : 'Save Payment'}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Confirm Status Update Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Status Update</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedParking && (
                        <p>
                            Are you sure you want to change the status from{' '}
                            <strong>{selectedParking.status}</strong> to{' '}
                            <strong>
                                {selectedAction 
                                    ? selectedAction.next 
                                    : Array.isArray(STATUS_ACTIONS[selectedParking.status]) 
                                        ? STATUS_ACTIONS[selectedParking.status][0].next
                                        : STATUS_ACTIONS[selectedParking.status]?.next
                                }
                            </strong>?
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={confirmStatusUpdate}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
                    {success}
                </Alert>
            )}
        </div>
    );
}

export default ParkingList;
